import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import { getUserProfile } from "../api/controller/userProfile";
import ProfileDialog from "./DialogBoxes/ProfileDialog";
import { Pencil } from "lucide-react";
import { FaHeart, FaFileAlt, FaTimes, FaDownload } from "react-icons/fa";
import resumeImage from "../assets/resumes/resume.webp";
import { isFavorite, ResumeCardData } from "../api/controller/videoController/resume";
import { API_PATHS } from "../api/paths";

// Resume Modal Component
const ResumeModal = ({ showModal, setShowResumeModal, selectedVideo }) => {
  if (!showModal) return null;

  const url = selectedVideo?.pdfURL || selectedVideo?.resumeURL || selectedVideo?.resume || selectedVideo?.fileURL || null;
  const ext = url ? (url.split('?')[0].split('.').pop() || '').toLowerCase() : '';

  return (
    <div className="fixed inset-0 flex justify-center items-center z-50 font-['corporate-s']">
      <div className="absolute inset-0 bg-black opacity-50"></div>
      <div className="relative bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-auto shadow-lg z-10">
        <button
          onClick={() => setShowResumeModal(false)}
          className="absolute top-4 right-4 text-gray-500 hover:text-red-500"
        >
          <FaTimes size={20} />
        </button>
        <h2 className="text-xl font-bold text-center mb-6 text-[#003DA5]">
          {selectedVideo.name}'s Resume
        </h2>

        {(() => {
          if (ext === 'pdf') {
            return (
              <div className="w-full max-h-[70vh] mb-4 overflow-auto">
                <iframe
                  src={url}
                  title={`${selectedVideo.name} resume`}
                  className="w-full h-[60vh] rounded-lg object-contain"
                />
                <div className="flex justify-center mt-4">
                  <a
                    href={url}
                    download
                    className="bg-[#003DA5] text-white py-2 px-4 rounded-lg font-semibold flex items-center gap-2"
                  >
                    <FaDownload /> Download
                  </a>
                </div>
              </div>
            );
          }

          if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
            return (
              <div className="w-full max-h-[70vh] mb-4 overflow-auto">
                <img
                  src={url}
                  alt={`${selectedVideo.name}'s Resume`}
                  className="w-full max-h-[60vh] object-contain rounded-lg"
                />
                <div className="flex justify-center mt-4">
                  <a
                    href={url}
                    download
                    className="bg-[#003DA5] text-white py-2 px-4 rounded-lg font-semibold flex items-center gap-2"
                  >
                    <FaDownload /> Download
                  </a>
                </div>
              </div>
            );
          }

          if (['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(ext)) {
            const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
            return (
              <div className="w-full max-h-[70vh] mb-4 overflow-auto">
                <iframe
                  src={viewerUrl}
                  title={`${selectedVideo.name} document`}
                  className="w-full h-[60vh] rounded-lg object-contain"
                />
                <div className="flex justify-center mt-4">
                  <a
                    href={url}
                    download
                    className="bg-[#003DA5] text-white py-2 px-4 rounded-lg font-semibold flex items-center gap-2"
                  >
                    <FaDownload /> Download
                  </a>
                </div>
              </div>
            );
          }

          return (
            <div className="w-full max-h-[70vh] mb-4 overflow-auto">
              <img
                src={resumeImage}
                alt="Resume placeholder"
                className="w-full max-h-[60vh] object-contain rounded-lg"
              />
              <div className="flex justify-center mt-4">
                {url ? (
                  <a
                    href={url}
                    download
                    className="bg-[#003DA5] text-white py-2 px-4 rounded-lg font-semibold flex items-center gap-2"
                  >
                    <FaDownload /> Download
                  </a>
                ) : (
                  <span className="text-gray-500">No resume file available</span>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

// Video Card Component
const VideoCard = ({ video, toggleFavorite, openResumeModal }) => {
  const skillsText = Array.isArray(video?.skills)
    ? video.skills.filter(Boolean).join(", ")
    : (video?.skills || "");

  const videoId = video?._id || video?.id;

  return (
    <div className="bg-white rounded-md shadow border-[0.5px] border-[#003DA5] hover:shadow-lg hover:scale-105 transition-transform duration-300 w-full min-h-[480px] relative flex flex-col">
      <video controls className="h-80 w-full flex-shrink-0 object-contain rounded-t-md">
        <source src={video.videoURL} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <div className="p-4 flex-grow flex flex-col justify-between">
        <div>
          <h2 className="text-[20px] text-[#003DA5] font-semibold font-['corporate-s'] mb-3 flex items-center gap-2">
            {video.name}
          </h2>
          <p className="flex items-center text-[#00244D] gap-2 font-semibold text-[12px] font-['corporate-s'] mt-2">
            Skills: <span className="text-gray-500">{skillsText}</span>
          </p>
        </div>
        <div className="flex justify-between items-center mt-4 pt-2 border-t border-gray-200">
          <button
            onClick={() => videoId && toggleFavorite(videoId)}
            className={`text-${video.isFavorite ? "red-600" : "gray-500"} hover:text-red-600 transition cursor-pointer`}
            title="Favorite"
          >
            <FaHeart size={15} />
          </button>
          <button
            onClick={() => openResumeModal(video)}
            className="text-gray-500 hover:text-blue-600 transition cursor-pointer"
            title="View Resume"
          >
            <FaFileAlt size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

const Profile = () => {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [regionOptions, setRegionOptions] = useState([]);
  const [regionFetchError, setRegionFetchError] = useState(null);

  const favorites = useMemo(
    () => resumes.filter((resume) => resume && resume.isFavorite),
    [resumes]
  );

  const assignedRegionLabels = useMemo(() => {
    if (!user || !user.regions) return [];
    const optionsById = new Map(
      (Array.isArray(regionOptions) ? regionOptions : []).map((option) => [option.id, option.label])
    );

    const normalized = Array.isArray(user.regions)
      ? user.regions
      : typeof user.regions === "string"
        ? user.regions.split(",").map((entry) => entry.trim()).filter(Boolean)
        : [];

    return normalized
      .map((region) => {
        if (!region) return null;
        if (typeof region === "string") {
          return optionsById.get(region) || region;
        }
        const id = region._id || region.id || region.cityId || region.city;
        if (id) {
          const label = optionsById.get(id.toString());
          if (label) return label;
        }
        const city = region.city || region.name || "";
        const state = region.state || region.region || "";
        const labelParts = [city, state].filter(Boolean);
        if (labelParts.length) return labelParts.join(", ");
        if (id) return id.toString();
        return null;
      })
      .filter(Boolean);
  }, [user, regionOptions]);

  const matchesResumeId = (resume, targetId) => {
    if (!resume || !targetId) return false;
    const resumeId = resume._id || resume.id;
    if (!resumeId) return false;
    return resumeId.toString() === targetId.toString();
  };

  useEffect(() => {
    async function fetchUserProfile() {
      try {
        setLoadError(null);
        const [userProfile, resumeList] = await Promise.all([
          getUserProfile(),
          ResumeCardData().catch((error) => {
            console.error("Failed to load resumes:", error);
            setLoadError("Unable to load favorite videos right now.");
            return [];
          }),
        ]);
        setUser(userProfile);
        setResumes(Array.isArray(resumeList) ? resumeList : []);
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
        setLoadError("Unable to load profile details right now.");
      }
    }
    fetchUserProfile();
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadRegions = async () => {
      try {
        setRegionFetchError(null);
        const token = localStorage.getItem("authToken");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(API_PATHS.regions, { headers });
        const raw = Array.isArray(response.data?.regions)
          ? response.data.regions
          : Array.isArray(response.data)
            ? response.data
            : [];
        const normalized = raw
          .map((region) => {
            if (!region) return null;
            const id = region._id || region.id || region.cityId || region.city;
            if (!id) return null;
            const city = region.city || region.name || "";
            const state = region.state || region.region || "";
            const labelParts = [city, state].filter(Boolean);
            return {
              id: id.toString(),
              label: labelParts.length ? labelParts.join(", ") : id.toString(),
              raw: region,
            };
          })
          .filter(Boolean);

        if (!ignore) {
          setRegionOptions(normalized);
        }
      } catch (error) {
        if (!ignore) {
          console.error("Failed to fetch regions:", error);
          setRegionFetchError("Unable to load regions at this time.");
          setRegionOptions([]);
        }
      }
    };

    loadRegions();

    return () => {
      ignore = true;
    };
  }, []);

  const toggleFavorite = async (videoId) => {
    const videoToUpdate = resumes.find((v) => matchesResumeId(v, videoId));
    if (!videoToUpdate) return;

    const newFavorite = !videoToUpdate.isFavorite;

    try {
      // Update server
      await isFavorite({
        _id: (videoToUpdate._id || videoToUpdate.id)?.toString(),
        isFavorite: newFavorite,
      });

      setResumes((prev) =>
        prev.map((resume) =>
          matchesResumeId(resume, videoId)
            ? { ...resume, isFavorite: newFavorite }
            : resume
        )
      );
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const openResumeModalHandler = (video) => {
    setSelectedVideo(video);
    setShowResumeModal(true);
  };

  if (!user) {
    return (
      <div className="flex flex-col justify-center items-center text-center p-6 space-y-4 min-h-screen">
        <h2 className="text-4xl font-['corporate-s'] text-[#00244D] mb-2">No profile found</h2>
        <p className="text-gray-600 mb-4 font-['corporate-s']">
          You need an account to access your profile.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate("/login")}
            className="bg-[#00244D] text-[#f0f0f0] px-6 py-3 rounded-lg font-['corporate-s']"
          >
            Create Account / Login
          </button>
          <button
            onClick={() => navigate("/")}
            className="bg-[#f0f0f0] text-[#00244D] px-6 py-3 rounded-lg font-['corporate-s']"
          >
            Continue without profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 font-['corporate-s'] min-h-screen">
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 h-48 md:h-56 w-full relative">
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
          <img
            src={user.profilePhoto}
            alt="Profile"
            className="w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-white shadow-lg object-cover"
          />
        </div>
      </div>

      <div className="flex justify-center px-4 mt-20">
        <div className="relative w-full max-w-4xl bg-white shadow-lg rounded-xl p-6 md:p-10 border border-gray-200">
          {/* <button
            type="button"
            onClick={() => setIsDialogOpen(true)}
            className="absolute top-4 right-4 inline-flex items-center gap-2 rounded-lg border border-[#003DA5] px-3 py-2 text-sm font-semibold text-[#003DA5] hover:bg-[#003DA5] hover:text-white transition-colors"
            aria-label="Edit profile"
          >
            <Pencil size={16} />
            <span>Edit</span>
          </button> */}
          <div className="flex flex-col items-center text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{user.fullName}</h2>
            <p className="text-gray-500">{user.userType}</p>
            {user.jobTitle ? (
              <p className="text-gray-600 mt-1">{user.jobTitle}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
            <div>
              <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wide">Username</label>
              <p className="text-lg font-medium text-gray-800">{user.userName}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wide">Email</label>
              <p className="text-lg font-medium text-gray-800">{user.email}</p>
            </div>
            {/* <div>
              <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wide">User ID</label>
              <p className="text-lg font-medium text-gray-800">{user.userId}</p>
            </div> */}
            <div>
              <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wide">Organization</label>
              <p className="text-lg font-medium text-gray-800">{user.organization || "Not provided"}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wide">Job Title</label>
              <p className="text-lg font-medium text-gray-800">{user.jobTitle || "Not provided"}</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wide">Phone Number</label>
              <p className="text-lg font-medium text-gray-800">{user.phoneNumber || "Not provided"}</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-500 uppercase tracking-wide">Regions</label>
              <p className="text-lg font-medium text-gray-800">
                {assignedRegionLabels.length ? assignedRegionLabels.join(" | ") : "Not provided"}
              </p>
              {regionFetchError ? (
                <p className="text-sm text-red-500 mt-1">{regionFetchError}</p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <section className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <h3 className="text-4xl font-['corporate-s'] text-center mb-8 text-[#00244D]">Favorite Videos</h3>
          {loadError ? (
            <p className="text-center text-red-600 text-sm sm:text-base">{loadError}</p>
          ) : null}
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mt-6 justify-items-center">
            {favorites.length > 0 ? (
              favorites.map((video, index) => (
                <VideoCard
                  key={video?._id || video?.id || `favorite-${index}`}
                  video={video}
                  toggleFavorite={toggleFavorite}
                  openResumeModal={openResumeModalHandler}
                />
              ))
            ) : (
              <p className="text-center text-gray-500 col-span-full text-sm sm:text-base">No favorite videos yet.</p>
            )}
          </div>
        </div>
      </section>

      <ResumeModal
        showModal={showResumeModal}
        setShowResumeModal={setShowResumeModal}
        selectedVideo={selectedVideo}
      />

      <ProfileDialog
        showModal={isDialogOpen}
        setShowModal={setIsDialogOpen}
        user={user}
        onSave={(updatedUser) => setUser(updatedUser)}
        regionOptions={regionOptions}
      />
    </div>
  );
};

Profile.propTypes = {
  userId: PropTypes.string,
};

export default Profile;